/**
 * Section Title API service.
 *
 * Centralizes all section-title-related API calls with consistent error handling
 * and TypeScript types.
 *
 * @module sectionTitleApi
 */

import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/apiUtils';

export interface SectionTitle {
  id: number;
  level: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
  module: 'reading' | 'listening' | 'writing' | 'speaking';
  section: number;
  text: string;
  examType: string[];
  material_type?: 'audio' | null;
  material_binary?: string | null;
  material_url?: string | null;
  material_extension?: string | null;
  material_mime_type?: string | null;
  weight?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface SectionTitleFormData {
  level: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
  module: 'reading' | 'listening' | 'writing' | 'speaking';
  section: number;
  text: string;
  examType: string[];
  material_type?: 'audio' | null;
  material_base64?: string;
  material_extension?: string;
  material_mime_type?: string;
  weight?: number | null;
}

export const getSectionTitlesList = async (): Promise<SectionTitle[]> => {
  const result = await apiGet<SectionTitle[]>('/section-titles/list', {
    errorMessage: 'Failed to fetch section titles',
    context: 'getSectionTitlesList',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to fetch section titles');
  }
  return result;
};

export const createSectionTitle = async (
  titleData: SectionTitleFormData,
): Promise<SectionTitle> => {
  const result = await apiPost<SectionTitle>('/section-titles/', titleData, {
    errorMessage: 'Failed to create section title',
    context: 'createSectionTitle',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to create section title');
  }
  return result;
};

export const updateSectionTitle = async (
  id: number,
  titleData: SectionTitleFormData,
): Promise<SectionTitle> => {
  const result = await apiPut<SectionTitle>(`/section-titles/${id}`, titleData, {
    errorMessage: 'Failed to update section title',
    context: 'updateSectionTitle',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to update section title');
  }
  return result;
};

export const deleteSectionTitle = async (id: number): Promise<void> => {
  await apiDelete(`/section-titles/${id}`, {
    errorMessage: 'Failed to delete section title',
    context: 'deleteSectionTitle',
    throwOnError: true,
  });
};

export const getSectionTitleFull = async (
  id: number,
): Promise<SectionTitle> => {
  const result = await apiGet<SectionTitle>(`/section-titles/${id}/full`, {
    errorMessage: 'Failed to fetch section title',
    context: 'getSectionTitleFull',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to fetch section title');
  }
  return result;
};

export const getSectionWeights = async (
  level: string,
  module: string,
  examType: string,
): Promise<Record<number, number>> => {
  const params = new URLSearchParams({
    level,
    module,
    exam_type: examType,
  });

  const weights =
    (await apiGet<Record<string, number>>(
      `/section-titles/weights?${params}`,
      {
        errorMessage: 'Failed to fetch section weights',
        context: 'getSectionWeights',
        errorReturnValue: {},
        suppressErrorToast: true,
      },
    )) ?? {};

  const result: Record<number, number> = {};
  Object.keys(weights).forEach((key) => {
    const sectionNum = Number.parseInt(key, 10);
    if (!Number.isNaN(sectionNum)) {
      result[sectionNum] = weights[key];
    }
  });

  return result;
};

export const bulkDeleteSectionTitles = async (ids: number[]): Promise<void> => {
  await apiDelete('/section-titles/bulk', {
    body: JSON.stringify({ ids }),
    errorMessage: 'Failed to bulk delete section titles',
    context: 'bulkDeleteSectionTitles',
    throwOnError: true,
  });
};
